import type { FormDataMap, FormElementConfig, FormFieldConfig, FormValues } from '../../types/form-builder.types';
import type { FieldState } from '../../types/form-store.types';
import { collectDependencies } from '../../utils/dependencies.utils';
import { resolveDynamicStringValue } from '../../utils/dynamic-value.utils';
import { resolveCondition } from '../../utils/resolve-condition.utils';
import { getDefaultValue, isFieldConfig, normalizeFieldValue } from './field.utils';
import { isFieldRequired } from './validation.utils';

export const buildElementMaps = (
    elements: readonly FormElementConfig[]
): {
    elementConfigs: Map<string, FormElementConfig>;
    fieldConfigs: Map<string, FormFieldConfig>;
} => {
    const elementConfigs = new Map<string, FormElementConfig>();
    const fieldConfigs = new Map<string, FormFieldConfig>();

    for (const element of elements) {
        elementConfigs.set(element.id, element);
        if (isFieldConfig(element)) {
            fieldConfigs.set(element.id, element);
        }
    }

    return { elementConfigs, fieldConfigs };
};

export const buildReverseDependencies = (elements: readonly FormElementConfig[]): Map<string, Set<string>> => {
    const reverseDependencies = new Map<string, Set<string>>();

    for (const element of elements) {
        const dependencies = collectDependencies(element);
        for (const dependency of dependencies) {
            const existing = reverseDependencies.get(dependency);
            if (existing) {
                existing.add(element.id);
            } else {
                reverseDependencies.set(dependency, new Set([element.id]));
            }
        }
    }

    return reverseDependencies;
};

export const initializeElementStates = (
    elements: readonly FormElementConfig[],
    defaultValues: FormValues | undefined,
    data?: FormDataMap
): { fieldStates: Map<string, FieldState>; initialValues: FormValues } => {
    const fields = elements.filter(isFieldConfig);
    const initialValues: FormValues = {};

    for (const field of fields) {
        initialValues[field.id] = normalizeFieldValue(field, defaultValues?.[field.id] ?? getDefaultValue(field));
    }

    const fieldStates = new Map<string, FieldState>();

    for (const element of elements) {
        const isField = isFieldConfig(element);
        const rendered =
            element.showWhen === undefined ? true : resolveCondition(element.showWhen, initialValues, data);
        const hidden =
            rendered && element.hidden !== undefined ? resolveCondition(element.hidden, initialValues, data) : false;

        fieldStates.set(element.id, {
            value: isField ? (initialValues[element.id] ?? getDefaultValue(element)) : '',
            error: undefined,
            isTouched: false,
            disabled:
                rendered && element.disabled !== undefined
                    ? resolveCondition(element.disabled, initialValues, data)
                    : false,
            hidden,
            helperText:
                rendered && isField
                    ? resolveDynamicStringValue({ value: element.helperText, formValues: initialValues, data })
                    : undefined,
            required: rendered && isField ? isFieldRequired(element, initialValues, data) : false,
            rendered,
            validating: false,
        });
    }

    return { fieldStates, initialValues };
};
